import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "glosor_drawing_pad";

export function getStoredPadEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setStoredPadEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // localStorage unavailable; keep the in-memory toggle only.
  }
}

type Point = { x: number; y: number };

const LINE_WIDTH = 3;
// Ignore sub-pixel jitter between pointermove samples.
const MIN_DIST = 1.5;

function strokeColor(): string {
  // Canvas can't inherit CSS theme colors, so resolve them from the same
  // .dark class the rest of the app keys off. Sky ink (the app accent)
  // keeps handwriting distinguishable from the slate card text underneath.
  return document.documentElement.classList.contains("dark") ? "#38bdf8" : "#0284c7";
}

function mid(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

// Draw one stroke as quadratic curves through segment midpoints (smooths the
// raw pointermove polyline). A single point renders as a dot.
function drawStroke(ctx: CanvasRenderingContext2D, pts: Point[]) {
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (!first || !last) return;
  if (pts.length === 1) {
    ctx.beginPath();
    ctx.arc(first.x, first.y, LINE_WIDTH / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i + 1 < pts.length; i++) {
    const curr = pts[i];
    const next = pts[i + 1];
    if (!curr || !next) break;
    const m = mid(curr, next);
    ctx.quadraticCurveTo(curr.x, curr.y, m.x, m.y);
  }
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

// Freehand ink overlay covering the whole study card, for practicing the
// answer by hand (finger, stylus, or mouse) — draw anywhere on the card,
// before or after revealing. Purely local, nothing is uploaded. Remount
// (via key) to clear it for the next card. The card header keeps its
// controls clickable by sitting above this overlay (relative z-10 in
// Study.tsx). Strokes anchor to the card's top-left, so they keep their
// position when the card grows on reveal.
export default function DrawingPad() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Point[][]>([]);
  const activeRef = useRef<{ pointerId: number; points: Point[] } | null>(null);
  const [strokeCount, setStrokeCount] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);

  function getCtx(): CanvasRenderingContext2D | null {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return null;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const color = strokeColor();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    return ctx;
  }

  function redraw() {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    for (const stroke of strokesRef.current) drawStroke(ctx, stroke);
    if (activeRef.current) drawStroke(ctx, activeRef.current.points);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
      redraw();
    };
    resize();
    // The card grows when the answer is revealed — keep the backing store in
    // step (resizing it clears the bitmap, so redraw the kept strokes).
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    // Redraw in the new stroke color when the theme is toggled mid-session.
    const mo = new MutationObserver(redraw);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toPoint(e: React.PointerEvent<HTMLCanvasElement>): Point {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (activeRef.current) return; // one pointer at a time
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    activeRef.current = { pointerId: e.pointerId, points: [toPoint(e)] };
    setIsDrawing(true);
    redraw();
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const active = activeRef.current;
    if (!active || e.pointerId !== active.pointerId) return;
    const p = toPoint(e);
    const prev = active.points[active.points.length - 1];
    if (!prev || Math.hypot(p.x - prev.x, p.y - prev.y) < MIN_DIST) return;
    active.points.push(p);
    // Repaint just the tail segment; a full redraw per move gets janky on
    // long strokes on slower phones.
    const ctx = getCtx();
    if (!ctx) return;
    const pts = active.points;
    const n = pts.length;
    const beforePrev = pts[n - 3];
    ctx.beginPath();
    if (n > 2 && beforePrev) {
      const m = mid(beforePrev, prev);
      ctx.moveTo(m.x, m.y);
    } else {
      ctx.moveTo(prev.x, prev.y);
    }
    const m2 = mid(prev, p);
    ctx.quadraticCurveTo(prev.x, prev.y, m2.x, m2.y);
    ctx.stroke();
  }

  function finishStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    const active = activeRef.current;
    if (!active || e.pointerId !== active.pointerId) return;
    activeRef.current = null;
    strokesRef.current.push(active.points);
    setStrokeCount(strokesRef.current.length);
    setIsDrawing(false);
    redraw();
  }

  function undoStroke() {
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    redraw();
  }

  function clearAll() {
    strokesRef.current = [];
    activeRef.current = null; // also drop a stroke mid-draw (second finger on Clear)
    setStrokeCount(0);
    setIsDrawing(false);
    redraw();
  }

  return (
    <div className="absolute inset-0 rounded-xl overflow-hidden">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
        onContextMenu={(e) => e.preventDefault()}
        className="absolute inset-0 w-full h-full touch-none select-none cursor-crosshair"
      />
      {(strokeCount > 0 || isDrawing) && (
        <div className="absolute bottom-1.5 right-1.5 flex gap-1.5">
          <button
            type="button"
            onClick={undoStroke}
            disabled={strokeCount === 0}
            title="Undo last stroke"
            className="btn-secondary !px-2.5 !py-1 text-sm disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↶
          </button>
          <button
            type="button"
            onClick={clearAll}
            title="Clear drawing"
            className="btn-secondary !px-2.5 !py-1 text-sm"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
